import { useEffect } from "react"
import { useForm, type Resolver } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation } from "urql"
import { toast } from "sonner"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Loader2 } from "lucide-react"
import { FieldError } from "@/components/shared/field-error"
import { streamSinkSchema, type StreamSinkFormValues } from "@/lib/schemas"
import { CreateStreamSinkMutation, UpdateStreamSinkMutation } from "@/lib/graphql/streaming"

const configExamples: Record<string, string> = {
  S3: JSON.stringify({
    bucket: "my-bucket",
    region: "us-east-1",
    prefix: "streams/",
    format: "jsonl",
  }, null, 2),
  WEBHOOK: JSON.stringify({
    url: "https://example.com/webhook",
    headers: { "Authorization": "Bearer token" },
  }, null, 2),
  POSTGRES: JSON.stringify({
    dsn: "postgres://user:pass@host:5432/db",
    table: "stream_data",
  }, null, 2),
}

interface SinkFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
  streamId: string
  initialData?: {
    id: string
    name: string
    sinkType: string
    config: Record<string, unknown>
    batchSize: number
    flushInterval: string
    status: string
  }
}

export function SinkForm({ open, onOpenChange, onSuccess, streamId, initialData }: SinkFormProps) {
  const isEdit = !!initialData
  const [, create] = useMutation(CreateStreamSinkMutation)
  const [, update] = useMutation(UpdateStreamSinkMutation)

  const form = useForm<StreamSinkFormValues>({
    resolver: zodResolver(streamSinkSchema) as Resolver<StreamSinkFormValues>,
    defaultValues: {
      streamId,
      name: initialData?.name ?? "",
      sinkType: (initialData?.sinkType as "S3" | "WEBHOOK" | "POSTGRES") ?? "S3",
      config: initialData ? JSON.stringify(initialData.config, null, 2) : configExamples.S3,
      batchSize: initialData?.batchSize ?? 100,
      flushInterval: initialData?.flushInterval ?? "60s",
      status: (initialData?.status as "ACTIVE" | "PAUSED") ?? "ACTIVE",
    },
  })

  useEffect(() => {
    if (open) {
      form.reset({
        streamId,
        name: initialData?.name ?? "",
        sinkType: (initialData?.sinkType as "S3" | "WEBHOOK" | "POSTGRES") ?? "S3",
        config: initialData ? JSON.stringify(initialData.config, null, 2) : configExamples.S3,
        batchSize: initialData?.batchSize ?? 100,
        flushInterval: initialData?.flushInterval ?? "60s",
        status: (initialData?.status as "ACTIVE" | "PAUSED") ?? "ACTIVE",
      })
    }
  }, [open, initialData, streamId, form])

  const sinkType = form.watch("sinkType")

  useEffect(() => {
    if (!isEdit && sinkType && configExamples[sinkType]) {
      form.setValue("config", configExamples[sinkType])
    }
  }, [sinkType, isEdit, form])

  const onSubmit = async (values: StreamSinkFormValues) => {
    const config = JSON.parse(values.config)

    if (isEdit) {
      const result = await update({
        id: initialData.id,
        input: {
          name: values.name,
          config,
          batchSize: values.batchSize,
          flushInterval: values.flushInterval,
          status: values.status as "ACTIVE" | "PAUSED",
        },
      })
      if (result.error) { toast.error(result.error.message); return }
      toast.success("Sink updated")
    } else {
      const result = await create({
        input: {
          streamId: values.streamId,
          name: values.name,
          sinkType: values.sinkType as "S3" | "WEBHOOK" | "POSTGRES",
          config,
          batchSize: values.batchSize,
          flushInterval: values.flushInterval,
        },
      })
      if (result.error) { toast.error(result.error.message); return }
      toast.success("Sink created")
    }

    onSuccess()
    onOpenChange(false)
    form.reset()
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Sink" : "Create Sink"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-4 rounded-lg glass-card p-4">
            <div className="space-y-2">
              <Label htmlFor="sinkName">Name</Label>
              <Input id="sinkName" {...form.register("name")} placeholder="my-s3-sink" />
              <FieldError error={form.formState.errors.name} />
            </div>

            {!isEdit && (
              <div className="space-y-2">
                <Label>Sink Type</Label>
                <Select
                  value={form.watch("sinkType")}
                  onValueChange={(v) => form.setValue("sinkType", v as "S3" | "WEBHOOK" | "POSTGRES")}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="S3">S3</SelectItem>
                    <SelectItem value="WEBHOOK">Webhook</SelectItem>
                    <SelectItem value="POSTGRES">Postgres</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="sinkConfig">Configuration (JSON)</Label>
              <Textarea
                id="sinkConfig"
                rows={6}
                className="font-mono text-xs"
                {...form.register("config")}
              />
              <FieldError error={form.formState.errors.config} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="batchSize">Batch Size</Label>
                <Input id="batchSize" type="number" {...form.register("batchSize")} />
                <FieldError error={form.formState.errors.batchSize} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="flushInterval">Flush Interval</Label>
                <Input id="flushInterval" {...form.register("flushInterval")} placeholder="60s" />
                <FieldError error={form.formState.errors.flushInterval} />
              </div>
            </div>

            {isEdit && (
              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={form.watch("status")}
                  onValueChange={(v) => form.setValue("status", v as "ACTIVE" | "PAUSED")}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ACTIVE">Active</SelectItem>
                    <SelectItem value="PAUSED">Paused</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : isEdit ? "Update" : "Create"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
